import { Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { IconButton } from '@/components/ui/IconButton'
import { Tag } from '@/components/ui/Tag'
import {
  useAmendRole,
  useCapabilityCatalogue,
  useCreateRole,
  useDeleteRole,
  useRoles,
} from '@/features/settings/api/useRoles'
import { RoleFormSheet } from '@/features/settings/components/RoleFormSheet'
import type { RoleChange, RoleRecord } from '@/types/domain'

/* Roles & permissions — the editable view.
 *
 * This replaces the read-only reference table that stood here while the five
 * roles were fixed (AD-7). Migration 0021 made roles rows, so the question this
 * screen answers changed from "what can Pricing see?" to "what should this role
 * be able to do?" — and the answer is now something a founder writes rather
 * than something the PRD did.
 *
 * The reference table has not gone away; it moved below, because "what do the
 * five shipped roles do" is still the question somebody asks first, and a
 * screen that opens on an empty role editor answers nothing.
 *
 * Two refusals are rendered as ABSENT actions rather than disabled ones, per
 * EXPERIENCE.md § Permission-Shaped UI: the superuser role has no delete
 * button, and a system role has no delete button. A role somebody still holds
 * DOES show one, because that refusal is about state a founder can change — the
 * error names the count and tells them to reassign.
 */

export function RolesPanel() {
  const { t } = useTranslation()
  const roles = useRoles()
  const catalogue = useCapabilityCatalogue()

  const createRole = useCreateRole()
  const amendRole = useAmendRole()
  const deleteRole = useDeleteRole()

  const [editing, setEditing] = useState<RoleRecord | undefined>()
  const [sheetOpen, setSheetOpen] = useState(false)

  function openCreate() {
    setEditing(undefined)
    setSheetOpen(true)
  }

  function openEdit(role: RoleRecord) {
    setEditing(role)
    setSheetOpen(true)
  }

  function submit(change: RoleChange) {
    const mutation = editing
      ? amendRole.mutateAsync({ key: editing.key, change })
      : createRole.mutateAsync(change)

    void mutation.then(() => {
      setSheetOpen(false)
      setEditing(undefined)
    })
  }

  const pending = createRole.isPending || amendRole.isPending
  // The sheet shows whichever mutation is actually in flight. Both are never
  // pending at once — the sheet does one or the other.
  const error = (editing ? amendRole.error : createRole.error)?.message

  return (
    <Card>
      <CardHeader
        action={
          <Button onClick={openCreate} iconLeft={Plus} size="sm">
            {t('settings.roles.create')}
          </Button>
        }
      >
        <CardTitle>{t('settings.roles.title')}</CardTitle>
      </CardHeader>

      <CardBody className="space-y-3">
        <p className="text-sm text-text-muted">{t('settings.roles.intro')}</p>

        {roles.isPending && <p className="text-sm text-text-muted">{t('settings.roles.loading')}</p>}

        {roles.isError && (
          <p className="text-sm text-negative">{t('settings.roles.loadFailed')}</p>
        )}

        {roles.data?.map((role) => (
          <div
            key={role.key}
            className="flex items-start justify-between gap-4 rounded-lg border border-border-subtle p-3"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-text-heading">{role.name}</span>
                {role.isSuperuser && (
                  <Tag tone="brand">{t('settings.roles.badges.superuser')}</Tag>
                )}
                {role.isSystem && !role.isSuperuser && (
                  <Tag>{t('settings.roles.badges.system')}</Tag>
                )}
              </div>

              <p className="truncate text-sm text-text-muted">{role.description}</p>

              <p className="mt-1 text-xs text-text-muted">
                {/* The superuser's stored capability list is empty — the flag is
                    what grants access — so printing a count would say "0
                    permissions" about the role that holds every one. */}
                {role.isSuperuser
                  ? t('settings.roles.summary.allPermissions')
                  : t('settings.roles.summary.counts', {
                      capabilities: role.capabilities.length,
                      surfaces: Object.keys(role.scopes).length,
                    })}
                {' · '}
                {t('settings.roles.summary.users', { count: role.userCount })}
              </p>
            </div>

            <div className="flex shrink-0 gap-1">
              <IconButton
                icon={Pencil}
                label={t('settings.roles.editAction', { name: role.name })}
                onClick={() => openEdit(role)}
              />
              {/* Absent, not disabled, for the two roles the server will never
                  delete — a greyed-out button invites a click that can only
                  fail (EXPERIENCE.md § Permission-Shaped UI).

                  A `danger` TEXT button rather than an icon: deleting a role is
                  destructive, and destructive actions always carry a word
                  (DESIGN.md § Icons). */}
              {!role.isSystem && !role.isSuperuser && (
                <Button
                  variant="danger"
                  size="sm"
                  iconLeft={Trash2}
                  onClick={() => void deleteRole.mutate(role.key)}
                >
                  {t('common.actions.delete')}
                </Button>
              )}
            </div>
          </div>
        ))}

        {deleteRole.isError && (
          <p className="text-sm text-negative">{t('settings.roles.deleteFailed')}</p>
        )}
      </CardBody>

      {/* The editor needs the server's vocabulary before it can render a single
          checkbox, so it is mounted only once the catalogue has arrived. */}
      {sheetOpen && catalogue.data && (
        <RoleFormSheet
          open={sheetOpen}
          onClose={() => {
            setSheetOpen(false)
            setEditing(undefined)
          }}
          role={editing}
          catalogue={catalogue.data}
          onSubmit={submit}
          pending={pending}
          error={error}
        />
      )}
    </Card>
  )
}
